import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../provider/AuthProvider.tsx";
import SectionHeader from "../../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../../components/Breadcrumbs/Breadcrumb.tsx";
import DatePicker from "../../../components/Forms/DatePicker/DatePicker.tsx";
import FadeAlert, { FadeAlertType } from "../../../components/FadeAlert/FadeAlert.tsx";
import SelectGroup from "../../../components/Forms/SelectGroup/SelectGroup.tsx";
import { labelClass, inputClass } from "../../../constants/formStyles.ts";
import type { CalcNavigationState } from "../../../types/calculatorTypes.ts";
import {
  DziIntegrationService,
  type DziCalcPremiumResponse,
  type DziConvertOfferRequest,
} from "../../../services/Integrations/DziIntegration.ts";

// ─── Constants ────────────────────────────────────────────────────────────────

const CAR_USAGE_OPTIONS: Record<string, string> = {
  "1": "Лична употреба",
  "2": "Учебен",
  "3": "Такси",
  "4": "На лизинг",
  "5": "Охранителна и детективска дейност",
  "6": "Транспорт на пари и ценности",
  "7": "Превоз на опасни стоки",
  "8": "За търговски цели",
  "9": "За производствени цели",
  "10": "Със специален режим на движение",
  "11": "Под наем",
  "12": "Състезателен",
  "13": "Фирмен автомобил за лична употреба",
  "14": "Оперативен лизинг",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col">
    <span className="text-body dark:text-bodydark text-[11px]">{label}</span>
    <span className="text-xs font-semibold text-black dark:text-white">
      {value ?? "—"}
    </span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Dzi: React.FC = () => {
  const { jwt } = useAuth();
  const location = useLocation();
  const calcState = location.state as CalcNavigationState | undefined;

  const today = moment().format("DD-MM-YYYY");
  const nextYear = moment().add(1, "year").format("DD-MM-YYYY");

  const [form, setForm] = useState(() => ({
    ekatte: calcState?.owner?.ekatte ?? "",
    startDate: today,
    endDate: nextYear,
    numInstallments: 1,
    carUsage: "1",
    rightHandDrive: false,
  }));

  const [calcLoading, setCalcLoading] = useState(false);
  const [calcResult, setCalcResult] = useState<DziCalcPremiumResponse | null>(null);
  const [issueForm, setIssueForm] = useState({ sticker: "", greenCard: "" });
  const [issueLoading, setIssueLoading] = useState(false);
  const [issuedData, setIssuedData] = useState<any | null>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>(FadeAlertType.info);

  const autoCalcRef = useRef(false);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // ── Calculate premium ──────────────────────────────────────────────────────

  const calcPremium = async () => {
    if (!calcState?.vehicle || !calcState?.owner) {
      setAlert("Липсват данни за МПС или клиент. Моля, върнете се към калкулатора.");
      setAlertType(FadeAlertType.error);
      return;
    }
    const { vehicle, owner } = calcState;

    const fmt = (d: string) =>
      moment(d, ["DD-MM-YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD");

    // Prefer the backend-mapped classification (holder.providers.dzi); else derive
    // from the raw owner person type.
    const personType = (calcState.holder?.providers?.dzi?.clientPersonType ??
      (owner.personType === "2" ? "LegalEntity" : "PhysicalPerson")) as
      | "PhysicalPerson"
      | "LegalEntity";

    setCalcLoading(true);
    setCalcResult(null);
    try {
      const result = await DziIntegrationService.calcPremium(jwt, {
        clientPid: owner.pid,
        clientPersonType: personType,
        clientCityCode: form.ekatte,
        owners: [{ pid: owner.pid, personType, cityCode: form.ekatte }],
        questions: [
          { questId: "4600.3", questAnswer: "2" },
          { questId: "4600.5", questAnswer: form.rightHandDrive ? "1" : "2" },
        ],
        insuranceBegin: `${fmt(form.startDate)}T00:00:00.000`,
        insuranceEnd: `${fmt(form.endDate)}T23:59:59.000`,
        numInstallments: form.numInstallments,
        vehicle: {
          regNo: vehicle.regNo,
          ocp12: vehicle.certNumber,
          ocp17: vehicle.firstRegistrationDate || undefined,
          chassis: vehicle.chassis || undefined,
          make: vehicle.make || undefined,
          model: vehicle.model || undefined,
          numSeats: vehicle.seats || undefined,
          ccmTon: vehicle.displacement || undefined,
          enginePower: vehicle.enginePower || undefined,
          prodYear: vehicle.productionYear || undefined,
          carAge: vehicle.age || undefined,
          carUsage: Number(form.carUsage),
          covers: [{ coverType: "TPL" }],
        },
      });
      setCalcResult(result);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при изчисляване");
      setAlertType(FadeAlertType.error);
    } finally {
      setCalcLoading(false);
    }
  };

  // ── Issue policy ──────────────────────────────────────────────────────────

  const issuePolicy = async () => {
    const policyID = calcResult?.policyID;
    if (!policyID) {
      setAlert("Няма policyID от изчисляването. Моля, натиснете ‘Изчисли премия’ първо.");
      setAlertType(FadeAlertType.error);
      return;
    }
    if (!issueForm.sticker.trim() || !issueForm.greenCard.trim()) {
      setAlert("Въведете номер на стикер и зелена карта.");
      setAlertType(FadeAlertType.warning);
      return;
    }
    setIssueLoading(true);
    try {
      const payload: DziConvertOfferRequest = {
        policyID,
        annexID: 0,
        sticker: issueForm.sticker.trim(),
        greenCard: issueForm.greenCard.trim(),
        eventType: "APPL_CONV",
      };
      const result = await DziIntegrationService.convertOfferToPolicy(jwt, payload);
      setIssuedData(result);
      setAlert(
        result?.policyNumber
          ? `Полицата е издадена успешно. № ${result.policyNumber}`
          : "Полицата е издадена успешно."
      );
      setAlertType(FadeAlertType.success);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при издаване");
      setAlertType(FadeAlertType.error);
    } finally {
      setIssueLoading(false);
    }
  };

  // Auto-calculate when arriving from calculator
  useEffect(() => {
    if (!calcState || autoCalcRef.current) return;
    autoCalcRef.current = true;
    const timer = setTimeout(() => calcPremium(), 300);
    return () => clearTimeout(timer);
  }, []);

  // ── Derived premium value ──────────────────────────────────────────────────

  // Pick the variant matching the requested installment count, falling back to [0].
  const premiumVariant =
    calcResult?.premiumVariants?.find(
      (v) => v.installmentOption === String(form.numInstallments)
    ) ?? calcResult?.premiumVariants?.[0];

  const premium =
    premiumVariant?.insuredObjects?.[0]?.covers?.[0]?.premium;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <Breadcrumb pageName="ДЗИ — Гражданска отговорност" />

      <FadeAlert alert={alert} type={alertType} onClose={() => setAlert(null)} />

      {/* ── Summary from calculator ── */}
      {calcState && (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
            <SectionHeader title="Данни за МПС" />
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 pt-2">
              <InfoRow label="Рег. номер" value={calcState.vehicle.regNo} />
              <InfoRow label="Шаси" value={calcState.vehicle.chassis} />
              <InfoRow label="Марка / Модел" value={`${calcState.vehicle.make} ${calcState.vehicle.model}`.trim()} />
              <InfoRow label="Куб. см" value={calcState.vehicle.displacement || "—"} />
              <InfoRow label="Мощност (к.с.)" value={calcState.vehicle.enginePower || "—"} />
              <InfoRow label="Места" value={calcState.vehicle.seats || "—"} />
              <InfoRow label="Год. производство" value={calcState.vehicle.productionYear || "—"} />
            </div>
          </fieldset>
          <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
            <SectionHeader title="Данни за клиент" />
            <div className="grid grid-cols-1 gap-y-2 p-3 pt-2">
              <div className="mb-1 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#005baa]/10 text-sm font-bold text-[#005baa]">
                  {calcState.owner.name.split(" ").slice(0, 2).map((w) => w[0]).join("")}
                </div>
                <div>
                  <p className="text-sm font-bold text-black dark:text-white">{calcState.owner.name}</p>
                  <p className="text-body dark:text-bodydark text-[11px]">
                    {calcState.owner.personType === "1" ? "Физическо лице" : "Юридическо лице"}
                  </p>
                </div>
              </div>
              <InfoRow label={calcState.owner.personType === "1" ? "ЕГН" : "ЕИК"} value={calcState.owner.pid} />
              {calcState.owner.address && <InfoRow label="Адрес" value={calcState.owner.address} />}
            </div>
          </fieldset>
        </div>
      )}

      {/* ── Policy parameters ── */}
      <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark mb-4 rounded-sm border bg-white">
        <SectionHeader title="Параметри на полицата" />
        <div className="grid grid-cols-1 gap-3 p-4 pt-2 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={labelClass}>Начало</label>
            <DatePicker
              name="startDate"
              value={form.startDate}
              onChange={(e) => set("startDate", e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Край</label>
            <DatePicker
              name="endDate"
              value={form.endDate}
              onChange={(e) => set("endDate", e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Брой вноски</label>
            <SelectGroup
              value={String(form.numInstallments)}
              onChange={(v) => set("numInstallments", Number(v))}
              options={{ "1": "1 вноска", "2": "2 вноски", "4": "4 вноски", "12": "12 вноски" }}
            />
          </div>
          <div>
            <label className={labelClass}>ЕКАТТЕ (код на град)</label>
            <input
              type="text"
              value={form.ekatte}
              onChange={(e) => set("ekatte", e.target.value)}
              placeholder="68134"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Ползване на МПС</label>
            <SelectGroup
              value={form.carUsage}
              onChange={(v) => set("carUsage", v)}
              options={CAR_USAGE_OPTIONS}
            />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <input
              type="checkbox"
              id="rightHandDrive"
              checked={form.rightHandDrive}
              onChange={(e) => set("rightHandDrive", e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="rightHandDrive" className={labelClass + " mb-0 cursor-pointer"}>
              Десен волан
            </label>
          </div>
        </div>

        <div className="flex justify-center gap-3 px-4 pb-4">
          <button
            onClick={calcPremium}
            disabled={calcLoading}
            className="flex items-center gap-2 rounded bg-[#005baa] px-6 py-2 text-sm font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
          >
            {calcLoading && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {calcLoading ? "Изчислява..." : "Изчисли премия"}
          </button>
        </div>
      </fieldset>

      {calcResult && (
        <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
          <SectionHeader title="Резултат от ДЗИ" />
          <div className="p-4">
            {premium !== undefined ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="rounded-sm bg-[#005baa]/10 px-6 py-4 text-center">
                    <p className="text-body text-[11px] uppercase tracking-wide">Обща премия</p>
                    {/* NOTE: DZI's calcPremium has no explicit currency field; the
                        system assumes EUR. Confirm DZI's quote currency. */}
                    <p className="text-2xl font-bold text-[#005baa]">{premium.toFixed(2)} EUR</p>
                    {form.numInstallments > 1 && (
                      <p className="text-body text-xs">
                        {(premium / form.numInstallments).toFixed(2)} EUR / вноска × {form.numInstallments}
                      </p>
                    )}
                  </div>
                  {calcResult.policyID && (
                    <div>
                      <p className="text-body text-xs">Policy ID (за издаване)</p>
                      <p className="font-mono text-sm font-bold text-black dark:text-white">{calcResult.policyID}</p>
                    </div>
                  )}
                </div>

                {/* Issue form */}
                {!issuedData && (
                  <div className="border-stroke dark:border-strokedark rounded-sm border p-3">
                    <p className="mb-3 text-sm font-semibold text-black dark:text-white">Издаване на полица</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className={labelClass}>Стикер (сер. номер)</label>
                        <input
                          type="text"
                          value={issueForm.sticker}
                          onChange={(e) => setIssueForm((p) => ({ ...p, sticker: e.target.value }))}
                          placeholder="G1960604355"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Зелена карта (сер. номер)</label>
                        <input
                          type="text"
                          value={issueForm.greenCard}
                          onChange={(e) => setIssueForm((p) => ({ ...p, greenCard: e.target.value }))}
                          placeholder="AC112275"
                          className={inputClass}
                        />
                      </div>
                    </div>
                    <button
                      onClick={issuePolicy}
                      disabled={issueLoading || !issueForm.sticker.trim() || !issueForm.greenCard.trim()}
                      className="mt-3 flex items-center gap-2 rounded bg-green-600 px-6 py-2 text-sm font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
                    >
                      {issueLoading ? "Издава..." : "Издай полица"}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <pre className="overflow-auto rounded bg-gray-50 p-3 text-xs dark:bg-gray-800">
                {JSON.stringify(calcResult, null, 2)}
              </pre>
            )}
          </div>
        </fieldset>
      )}
    </>
  );
};

export default Dzi;
